import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../hooks/useDashboard';
import PisoStatusOverview from '../components/PisoStatusOverview';
import Chevron from '../components/Chevron';
import DateRangeFilter, { DateFilterOptions } from '../components/DateRangeFilter';
import { getEffectiveDateRange, getDateRangeLabel, toIsoBounds } from '../utils/dateFilterUtils';
import {
  DIAS_OLVIDADA,
  ESTADO_INFO,
  ORDEN_ESTADO,
  detalleEstado,
  estaOlvidada,
  estadoVisible,
  formatMinutos,
  minutosDesde,
  nombreSinPiso,
} from '../utils/estadoArea';
import { solicitudAseoService } from '../services/solicitudAseoService';
import { registroLimpiezaService } from '../services/registroLimpiezaService';
import { AreaEstado } from '../services/dashboardService';

type AreaConPiso = AreaEstado & { pisoNombre: string };

const formatHora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

const formatFechaHora = (iso: string) => {
  const d = new Date(iso);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return mismoDia
    ? `Hoy ${formatHora(iso)}`
    : d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const TONOS_TILE = {
  rojo: { borde: 'border-red-500', texto: 'text-red-700' },
  amarillo: { borde: 'border-yellow-400', texto: 'text-yellow-700' },
  verde: { borde: 'border-green-500', texto: 'text-green-700' },
  azul: { borde: 'border-indigo-500', texto: 'text-indigo-700' },
};

// Una limpieza abierta hace más de esto ya no está "en curso": quedó sin cerrar
const MINUTOS_EN_CURSO = 180;

// Más filas que esto convierten las alertas en un listado que nadie lee
const LIMITE_ALERTAS = 6;

const Tile: React.FC<{
  valor: React.ReactNode;
  titulo: string;
  detalle?: string;
  tono: keyof typeof TONOS_TILE;
  to?: string;
}> = ({ valor, titulo, detalle, tono, to }) => {
  const contenido = (
    <>
      <div className="text-sm font-medium text-gray-600">{titulo}</div>
      <div className={`mt-1 text-3xl sm:text-4xl font-bold ${TONOS_TILE[tono].texto}`}>{valor}</div>
      {detalle && <div className="mt-1 text-xs sm:text-sm text-gray-500">{detalle}</div>}
    </>
  );
  const clase = `block bg-white shadow rounded-lg p-4 border-l-4 ${TONOS_TILE[tono].borde}`;
  return to ? (
    <Link to={to} className={`${clase} hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500`}>{contenido}</Link>
  ) : (
    <div className={clase}>{contenido}</div>
  );
};

const Dashboard: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<DateFilterOptions>({ mode: 'preset', preset: 'hoy' });
  const rango = useMemo(() => toIsoBounds(getEffectiveDateRange(dateFilter)), [dateFilter]);

  const {
    metrics,
    alertas,
    estadosPorPiso,
    estadosActualizadosEn,
    isLoadingMetrics,
    isLoadingEstados,
    error,
  } = useDashboard(rango);

  const { data: solicitudes } = useQuery({
    queryKey: ['dashboard-solicitudes'],
    queryFn: solicitudAseoService.getAll,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
  });

  const { data: ultimasRes, isLoading: isLoadingUltimas } = useQuery({
    queryKey: ['dashboard-ultimas-limpiezas'],
    queryFn: () => registroLimpiezaService.getRegistrosLimpiezaPaginated(1, 8),
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
  });
  const ultimas = ultimasRes?.data?.data ?? [];

  const solicitudesPendientes = useMemo(
    () => (solicitudes ?? [])
      .filter((s) => s.activo !== false && !s.atendida)
      .sort((a, b) => new Date(b.fechaSolicitud).getTime() - new Date(a.fechaSolicitud).getTime()),
    [solicitudes]
  );
  const areasConReporte = useMemo(() => new Set(solicitudesPendientes.map((s) => s.areaId)), [solicitudesPendientes]);

  const resumen = useMemo(() => {
    const areas: AreaConPiso[] = (estadosPorPiso ?? []).flatMap((p) =>
      p.areas.filter((a) => a.activo !== false).map((a) => ({ ...a, pisoNombre: p.pisoNombre }))
    );
    const cuenta = (e: ReturnType<typeof estadoVisible>) => areas.filter((a) => estadoVisible(a) === e).length;
    const pausadas = cuenta('pausada');
    const ahora = Date.now();
    const atencion: AreaConPiso[] = [];
    const olvidadas: AreaConPiso[] = [];
    areas.forEach((a) => {
      const e = estadoVisible(a);
      if (e === 'rojo' && estaOlvidada(a, ahora) && !areasConReporte.has(a.id)) olvidadas.push(a);
      else if (e === 'rojo' || e === 'amarillo') atencion.push(a);
    });
    atencion.sort((a, b) => {
      const porReporte = Number(areasConReporte.has(b.id)) - Number(areasConReporte.has(a.id));
      if (porReporte !== 0) return porReporte;
      const porEstado = ORDEN_ESTADO[estadoVisible(a)] - ORDEN_ESTADO[estadoVisible(b)];
      if (porEstado !== 0) return porEstado;
      if (estadoVisible(a) === 'amarillo') return (a.minutosRestantesRojo ?? 0) - (b.minutosRestantesRojo ?? 0);
      // Urgentes: primero las que llevan más tiempo sin limpiar
      return (minutosDesde(b.ultimaLimpieza, ahora) ?? Infinity) - (minutosDesde(a.ultimaLimpieza, ahora) ?? Infinity);
    });
    return {
      total: areas.length,
      rojo: cuenta('rojo'),
      amarillo: cuenta('amarillo'),
      verde: cuenta('verde'),
      pausadas,
      // Fuera del horario laboral el backend deja todas las áreas en "Desactivada"
      fueraDeHorario: areas.length > 0 && pausadas === areas.length,
      atencion,
      olvidadas,
    };
  }, [estadosPorPiso, areasConReporte]);

  const [verTodasAlertas, setVerTodasAlertas] = useState(false);
  const [verOlvidadas, setVerOlvidadas] = useState(false);
  const alertasVisibles = verTodasAlertas ? resumen.atencion : resumen.atencion.slice(0, LIMITE_ALERTAS);

  const totalRegistros = metrics?.totalRegistros ?? 0;
  const registrosCompletados = metrics?.registrosCompletados ?? 0;
  const porcentajeFinalizadas = totalRegistros > 0
    ? Math.round((registrosCompletados / totalRegistros) * 100)
    : null;

  const hoyTexto = new Date().toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Estado de limpieza</h1>
          <p className="mt-1 text-sm text-gray-600 first-letter:uppercase">
            {hoyTexto}
            {estadosActualizadosEn ? ` · actualizado ${formatHora(new Date(estadosActualizadosEn).toISOString())}` : ''}
            {' · se actualiza cada minuto'}
          </p>
        </div>
        <Link
          to="/reportes"
          className="self-start sm:self-auto inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          Generar reportes
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
          No se pudieron cargar algunos datos. Se reintentará automáticamente.
        </div>
      )}

      {resumen.fueraDeHorario && (
        <div className="bg-gray-100 border border-gray-200 text-gray-700 rounded-lg p-4 text-sm">
          Fuera del horario laboral: las áreas quedan en pausa y no cambian de estado.
        </div>
      )}

      {/* Lo más importante, en números grandes */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Tile
          tono="rojo"
          titulo="Urgentes"
          valor={isLoadingEstados ? '–' : resumen.rojo}
          detalle={resumen.olvidadas.length > 0
            ? `${resumen.olvidadas.length} sin limpiar hace más de ${DIAS_OLVIDADA} días`
            : 'Áreas por limpiar ya'}
        />
        <Tile tono="amarillo" titulo="Atención" valor={isLoadingEstados ? '–' : resumen.amarillo} detalle="Se vencen pronto" />
        <Tile
          tono="verde"
          titulo="Al día"
          valor={isLoadingEstados ? '–' : <>{resumen.verde}<span className="text-lg sm:text-xl font-semibold text-gray-400"> / {resumen.total}</span></>}
          detalle={resumen.pausadas > 0 && !resumen.fueraDeHorario ? `${resumen.pausadas} en pausa` : 'Áreas activas'}
        />
        <Tile
          tono="azul"
          titulo="Reportes de pacientes"
          valor={solicitudes ? solicitudesPendientes.length : '–'}
          detalle="Sin atender · ver solicitudes"
          to="/solicitudes"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Necesitan atención ahora */}
        <section className="lg:col-span-2 bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Necesitan atención ahora</h2>
          {isLoadingEstados ? (
            <div className="mt-4 space-y-2 animate-pulse">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}
            </div>
          ) : (
            <>
              {resumen.atencion.length === 0 ? (
                <div className="mt-4 rounded-md bg-green-50 border border-green-200 text-green-800 p-4">
                  {resumen.fueraDeHorario
                    ? 'Sin alertas: fuera del horario laboral.'
                    : resumen.olvidadas.length > 0
                      ? 'Las áreas que se limpian a diario están al día.'
                      : 'Todo al día. No hay áreas urgentes ni por vencer.'}
                </div>
              ) : (
                <ul className="mt-2 divide-y divide-gray-100">
                  {alertasVisibles.map((area) => {
                    const estado = estadoVisible(area);
                    return (
                      <li key={area.id} className="py-2.5 flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ESTADO_INFO[estado].punto}`} aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-medium text-gray-900">{nombreSinPiso(area.nombre, area.pisoNombre)}</span>
                            <span className="text-sm text-gray-500">{area.pisoNombre}</span>
                            {areasConReporte.has(area.id) && (
                              <span className="text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">
                                Reportada por paciente
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{detalleEstado(area)}</div>
                        </div>
                        <span className={`text-xs font-semibold rounded px-2 py-1 border ${ESTADO_INFO[estado].chip}`}>
                          {ESTADO_INFO[estado].etiqueta}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}

              {resumen.atencion.length > LIMITE_ALERTAS && (
                <button
                  type="button"
                  onClick={() => setVerTodasAlertas((v) => !v)}
                  className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                >
                  {verTodasAlertas ? 'Mostrar menos' : `Ver las ${resumen.atencion.length - LIMITE_ALERTAS} restantes`}
                </button>
              )}

              {/* Áreas que llevan días sin limpiarse: van aparte para no tapar a las de la rutina */}
              {resumen.olvidadas.length > 0 && (
                <div className="mt-4 rounded-md border border-gray-200 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setVerOlvidadas((v) => !v)}
                    aria-expanded={verOlvidadas}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span>
                      <span className="block font-medium text-gray-900">
                        {resumen.olvidadas.length} {resumen.olvidadas.length === 1 ? 'área' : 'áreas'} sin limpiar hace más de {DIAS_OLVIDADA} días
                      </span>
                      <span className="block text-sm text-gray-600">Si ya no se usan, pausarlas en Áreas las saca de las alertas.</span>
                    </span>
                    <Chevron abierto={verOlvidadas} />
                  </button>
                  {verOlvidadas && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 border-t border-gray-200 px-4 py-2">
                      {resumen.olvidadas.map((area) => (
                        <li key={area.id} className="py-1.5 flex items-baseline justify-between gap-3 text-sm min-w-0">
                          <span className="truncate text-gray-900" title={area.nombre}>
                            {nombreSinPiso(area.nombre, area.pisoNombre)}
                            <span className="text-gray-500"> · {area.pisoNombre}</span>
                          </span>
                          <span className="text-gray-500 whitespace-nowrap">
                            {area.ultimaLimpieza ? `hace ${formatMinutos(minutosDesde(area.ultimaLimpieza) ?? 0)}` : 'nunca'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {/* Reportes de pacientes */}
        <section className="bg-white shadow rounded-lg p-4 sm:p-6">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Reportes de pacientes</h2>
            <Link to="/solicitudes" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Ver todos</Link>
          </div>
          {!solicitudes ? (
            <div className="mt-4 space-y-2 animate-pulse">
              {[1, 2].map((i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}
            </div>
          ) : solicitudesPendientes.length === 0 ? (
            <p className="mt-4 text-sm text-gray-600">No hay reportes sin atender.</p>
          ) : (
            <ul className="mt-3 divide-y divide-gray-100">
              {solicitudesPendientes.slice(0, 5).map((s) => (
                <li key={s.id} className="py-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-gray-900">{s.areaNombre || 'Área'}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      hace {formatMinutos(minutosDesde(s.fechaSolicitud) ?? 0)}
                    </span>
                  </div>
                  {s.observaciones && <p className="text-sm text-gray-600 line-clamp-2">"{s.observaciones}"</p>}
                </li>
              ))}
              {solicitudesPendientes.length > 5 && (
                <li className="pt-3 text-sm text-gray-500">y {solicitudesPendientes.length - 5} más</li>
              )}
            </ul>
          )}
        </section>
      </div>

      <PisoStatusOverview pisosStatus={estadosPorPiso ?? []} isLoading={isLoadingEstados} />

      {/* Actividad del período */}
      <section className="bg-white shadow rounded-lg p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Actividad</h2>
        <p className="text-sm text-gray-600 mb-4">Limpiezas registradas en el período: {getDateRangeLabel(dateFilter).toLowerCase()}.</p>
        <DateRangeFilter value={dateFilter} onChange={setDateFilter} className="mb-4" />

        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="rounded-md bg-gray-50 p-4">
            <dt className="text-sm text-gray-600">Limpiezas registradas</dt>
            <dd className="mt-1 text-2xl sm:text-3xl font-bold text-gray-900">{isLoadingMetrics || !metrics ? '–' : totalRegistros}</dd>
          </div>
          <div className="rounded-md bg-gray-50 p-4">
            <dt className="text-sm text-gray-600">Finalizadas</dt>
            <dd className="mt-1 text-2xl sm:text-3xl font-bold text-gray-900">
              {isLoadingMetrics || !metrics ? '–' : registrosCompletados}
              {porcentajeFinalizadas != null && (
                <span className="ml-2 text-base font-semibold text-gray-500">{porcentajeFinalizadas}%</span>
              )}
            </dd>
          </div>
          <div className="rounded-md bg-gray-50 p-4">
            <dt className="text-sm text-gray-600">Sin cerrar</dt>
            <dd className="mt-1 text-2xl sm:text-3xl font-bold text-gray-900">{alertas ? alertas.registrosSinFinalizar : '–'}</dd>
            <dd className="text-xs text-gray-500">Iniciadas y nunca finalizadas, de cualquier fecha</dd>
          </div>
        </dl>

        <h3 className="mt-6 text-base font-semibold text-gray-900">Últimas limpiezas</h3>
        {isLoadingUltimas ? (
          <div className="mt-3 space-y-2 animate-pulse">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}
          </div>
        ) : ultimas.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600">Aún no hay limpiezas registradas.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100">
            {ultimas.map((r) => (
              <li key={r.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div className="min-w-0">
                  <span className="font-medium text-gray-900">{r.areaNombre || 'Área'}</span>
                  <span className="text-sm text-gray-500"> · {r.usuarioNombre || 'Sin usuario'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-500">{formatFechaHora(r.fechaHoraInicio)}</span>
                  {r.fechaHoraFin ? (
                    <span className="text-green-700">Finalizada</span>
                  ) : (minutosDesde(r.fechaHoraInicio) ?? 0) < MINUTOS_EN_CURSO ? (
                    <span className="text-indigo-700">En curso</span>
                  ) : (
                    <span className="text-orange-600">Sin cerrar</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3">
          <Link to="/registros" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Ver todos los registros</Link>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
